import React, { useEffect, useState, useCallback } from 'react';
import { useSelector } from 'react-redux';
import moment from 'moment-timezone';

import { Card, Col, Row } from 'react-bootstrap';
import { Modal } from 'bootstrap';
import Swal from 'sweetalert2';
import CircleLoader from "react-spinners/CircleLoader";
import BeatLoader from 'react-spinners/BeatLoader';

import AdvanceTable from 'components/common/advance-table/AdvanceTable';
import AdvanceTablePagination from 'components/common/advance-table/AdvanceTablePagination';
import AdvanceTableWrapper from 'components/common/advance-table/AdvanceTableWrapper';

import { createUser, deleteUserById, fetchUsers, updateUserPassword } from 'api/users/users';
import { deleteCertificationById } from 'api/certifications/certifications';

import {
	exportCandidates,
	getCandidateType,
	getCertificationStatus,
	getCertificationType,
	getStatusBadge,
	handleApiError,
	successNotification
} from 'utils';

import Header from './header/Header';
import CandidatesColumns from './columns/CandidatesColumns';
import CandidateModal from '../modals/CandidateModal';

const override = {
	display: "block",
	margin: "0 auto",
	borderColor: "red"
};

/**
 * @component CandidatesTable
 * @description Componente principal para gestionar y mostrar candidatos registrados en una tabla avanzada.
 * Permite listar, registrar, editar, cargar por archivo y eliminar candidatos. También muestra modales con Bootstrap
 * y confirmaciones con SweetAlert2. Incluye funcionalidad de paginación, ordenamiento y carga de datos.
 *
 * @returns {JSX.Element}
 *
 * @example
 * <CandidatesTable />
 */
const CandidatesTable = () => {
	const token = useSelector(state => state.authentication.user?.token);

	// eslint-disable-next-line
	const [show, setShow] = useState(false);
	const [candidates, setCandidates] = useState([]);
	const [rows, setRows] = useState([]);
	const [isModalOpen, setIsModalOpen] = useState(false);
	const [activeModal, setActiveModal] = useState("");
	const [action, setAction] = useState("");
	const [candidateId, setCandidateId] = useState(0);
	const [certificationId, setCertificationId] = useState(0);
	const [uploadCandidates, setUploadCandidates] = useState(false);
	const [loading, setLoading] = useState(false);

	const [selectedRows, setSelectedRows] = useState({});


	/**
	 * @function useEffect
	 * @description Formatea los datos crudos de los candidatos para la tabla.
	 */
	useEffect(() => {
		if (candidates.length > 0) {
			let index = 0;
			const formatted = candidates.map(candidate => ({
				index: index++,
				user_id: candidate.id,
				certification_id: candidate.certification_id,
				username: candidate.username,
				email: candidate.email,
				name: `${candidate.name} ${candidate.lastname}`,
				college_name: candidate.college_name,
				catalog_id: candidate.catalog_id === 1 ? "EPSt" : "EMeT",
				certification_type: getCertificationType(candidate.certification_type),
				candidate_type: getCandidateType(candidate.candidate_type) ,
				certification_status: getCertificationStatus(candidate.certification_status),
				status: getStatusBadge(candidate.status),
				assigned_date: candidate.assigned_date ? moment.utc(candidate.assigned_date).format('DD/MM/YYYY') : 'N/A',
				closed_date: candidate.closed_date ? moment.utc(candidate.closed_date).format('DD/MM/YYYY') : 'N/A',
				completed_date: candidate.completed_date ? moment(candidate.completed_date).format('DD/MM/YYYY') : 'N/A'
			}));
			setRows(formatted);
		}
	}, [candidates]);

	/**
	 * @function useEffect
	 * @description Controla la apertura de los modales de Bootstrap.
	 */
	useEffect(() => {
		if (isModalOpen && activeModal === 'Candidato') {
			let modal = new Modal(document.getElementById('candidateModal'));
			modal.show();
		} else if (isModalOpen && activeModal === 'Importar') {
			let modal = new Modal(document.getElementById('candidatesModal'));
			modal.show();
		}
	}, [isModalOpen, activeModal]);


	/**
	 * @function loadCandidatesUsers
	 * @description Obtiene la lista de candidatos del backend.
	 */
	const loadCandidatesUsers = useCallback(async () => {
		try {
			setLoading(true);
			const { data } = await fetchUsers({ type: 'candidates' }, token);
			setCandidates(data || []); // Asegura que al menos sea un array vacío
		} catch (error) {
			handleApiError(error);
			setCandidates([]); // En caso de error también aseguras consistencia
		} finally {
			setLoading(false);
		}
	}, [token]);
	

	/**
	 * @function useEffect
	 * @description Carga los candidatos al montar el componente si hay token disponible.
	 */
	useEffect(() => {
		if (token) loadCandidatesUsers();
	}, [token, loadCandidatesUsers]);

	/**
	 * @function handleDeleteCertification
	 * @description Elimina la certificación y posteriormente el usuario.
	 */
	const handleDeleteCertification = async (user_id, certification_id) => {
		try {
			const response = await deleteCertificationById(certification_id, token);
			if (response.data.status === 200) 
				handleDeleteUser(user_id);
		} catch (error) {
			handleApiError(error);
		}
	};

	/**
	 * @function handleUpdateCandidatePassword
	 * @description
	 * Llama al endpoint para restablecer la contraseña de un candidato. Utiliza el `user_id` y un objeto con
	 * datos necesarios para el envío del correo (nombre, usuario, correo). Si la operación es exitosa,
	 * muestra una notificación de éxito. En caso de error, maneja la excepción mediante `handleApiError`.
	 *
	 * @async
	 * @param {number} user_id - ID del candidato cuya contraseña será restablecida.
	 *
	 * @returns {Promise<void>}
	 */
	const handleUpdateCandidatePassword = async (user_id) => {
		try {
			await updateUserPassword(user_id, {}, token);
			successNotification("Contraseña actualizada con éxito.");
		} catch (error) {
			handleApiError(error);
		}
	};

	/**
	 * @function handleDeleteUser
	 * @description Elimina al usuario y actualiza la tabla.
	 */
	const handleDeleteUser = async (id) => {
			try {
				const { data } = await deleteUserById(id, token);
				successNotification(data.message);
				loadCandidatesUsers();
			} catch (error) {
				handleApiError(error);
			}
		};
	

	/**
	 * @function handleEdit
	 * @description Abre el modal para editar un candidato.
	 */
	const handleEdit = (user_id, certification_id) => {
		setIsModalOpen(true);
		setActiveModal("Candidato");
		setAction("Editar");
		setCandidateId(user_id);
		setCertificationId(certification_id);
	};

	
	/**
	 * @function handleResetPassword
	 * @description
	 * Muestra un modal de confirmación para restablecer la contraseña de un candidato. Si el usuario confirma,
	 * se genera un objeto con los datos del candidato (`name`, `username`, `email`) y se llama a la función 
	 * `handleUpdateCandidatePassword` pasando el `user_id` y dichos datos.
	 * 
	 * El restablecimiento de la contraseña generará una nueva clave aleatoria y enviará un correo electrónico
	 * con las nuevas credenciales al candidato correspondiente.
	 *
	 * @param {number} user_id - ID del usuario al que se le desea restablecer la contraseña.
	 * @param {string} name - Nombre completo del candidato.
	 * @param {string} username - Nombre de usuario del candidato.
	 * @param {string} email - Correo electrónico del candidato.
	 *
	 * @example
	 * handleResetPassword(12, 'Juan Pérez', 'juan.perez', 'juan@example.com');
	 */
	const handleResetPassword = (user_id, name, username, email) => {
		Swal.fire({
			title: `¿Restablecer contraseña?`,
			text: `Esta acción generará una nueva contraseña y la enviará al correo al candidato ${name}.`,
			icon: "question",
			confirmButtonText: 'Continuar',
			cancelButtonText: 'Cancelar',
			showCancelButton: true,
			showCloseButton: true,
			allowOutsideClick: false,
			allowEscapeKey: false,
			allowEnterKey: false,
			reverseButtons: true
		}).then((result) => {
			if (result.isConfirmed) {
				let data = {
					name: name,
					username: username,
					email: email
				}
				handleUpdateCandidatePassword(user_id, data);
			}
		});	
	};

	/**
	 * @function handleDelete
	 * @description Muestra alerta para confirmar si se debe eliminar el candidato.
	 */
	const handleDelete = (user_id, certification_id, name, certification_status) => {

		Swal.fire({
			title: `¿Deseas eliminar al candidato ${name}?`,
			text: "Una vez eliminado ya no estará disponible este usuario.",
			icon: "question",
			confirmButtonText: 'Continuar',
			cancelButtonText: 'Cancelar',
			showCancelButton: true,
			showCloseButton: true,
			allowOutsideClick: false,
			allowEscapeKey: false,
			allowEnterKey: false,
			reverseButtons: true
		}).then((result) => {
			if (result.isConfirmed) {
				if (certification_status === "PENDIENTE")
					confirmDeleteCandidate(user_id, certification_id);
				else
					certificationStarted(user_id, certification_id);
			}
		});
	};

	/**
	 * @function certificationStarted
	 * @description Alerta para confirmar eliminación si el candidato ya comenzó la certificación.
	 */
	const certificationStarted = (user_id, certification_id) => {
		Swal.fire({
			title: "¿Estás seguro de eliminar al candidato?",
			text: "El candidato ya ha comenzado la certificación. Si eliminas este usuario, se perderá el acceso y todos los datos relacionados con la certificación.",
			icon: "warning",
			confirmButtonText: 'Eliminar',
			cancelButtonText: 'Cancelar',
			showCancelButton: true,
			showCloseButton: true,
			allowOutsideClick: false,
			allowEscapeKey: false,
			allowEnterKey: false,
			reverseButtons: true
		}).then((result) => {
			if (result.isConfirmed) confirmDeleteCandidate(user_id, certification_id);
		});
	};

	/**
	 * @function confirmDeleteCandidate
	 * @description Confirmación final para eliminar un candidato.
	 */
	const confirmDeleteCandidate = (user_id, certification_id) => {
		Swal.fire({
			title: "¿Estás seguro?",
			text: "Esta operación no se puede deshacer.",
			icon: "warning",
			confirmButtonText: 'Eliminar',
			cancelButtonText: 'Cancelar',
			showCancelButton: true,
			showCloseButton: true,
			allowOutsideClick: false,
			allowEscapeKey: false,
			allowEnterKey: false,
			reverseButtons: true
		}).then((result) => {
			if (result.isConfirmed) handleDeleteCertification(user_id, certification_id);
		});
	};

	/**
	 * @function handleOpenModal
	 * @description Abre el modal para registrar un nuevo candidato.
	 */
	const handleOpenModal = () => {
		setIsModalOpen(true);
		setActiveModal("Candidato");
		setAction("Nuevo");
		setCandidateId(0);
	};

	/**
	 * @function handleCloseModal
	 * @description Cierra el modal y recarga los candidatos.
	 */
	const handleCloseModal = () => {
		setIsModalOpen(false);
		setActiveModal("");
		document.body.style.overflow = 'auto';
		loadCandidatesUsers();
	};

	/**
	 * @function handleSubmitCandidates
	 * @description Envía el archivo de carga masiva de candidatos.
	 */
	const handleSubmitCandidates = async (fileData) => {
		setUploadCandidates(true);
		try {
			await createUser(fileData, token);
			loadCandidatesUsers();
			successNotification("Candidatos cargados correctamente.");
		} catch (error) {
			handleApiError(error);
		} finally {
			setUploadCandidates(false);
		}
	};

	/**
	 * @function handleSelectedChange
	 * @description
	 * Maneja el cambio de selección en la tabla. Extrae los `index` seleccionados desde `selectedData`
	 * y filtra las filas (`rows`) que coincidan con esos índices. Actualiza el estado `selectedRows` con las filas seleccionadas.
	 *
	 * Esta función está memorizada con `useCallback` para evitar recrearse innecesariamente,
	 * y depende del estado actual de `rows`.
	 *
	 * @param {Object} selectedData - Objeto con las claves de las filas seleccionadas, por ejemplo: { '0': true, '2': true }.
	 * Las claves representan los índices (`index`) de las filas seleccionadas.
	 *
	 * @returns {void}
	 */
	const handleSelectedChange = useCallback((selectedData) => {
		const selectedIds = Object.keys(selectedData).map(id => parseInt(id, 10));
		setSelectedRows(rows.filter(row => selectedIds.includes(row.index)));
	}, [rows]);

	/**
	 * @function handleBulkAction
	 * @description
	 * Ejecuta acciones en lote según la selección (exportar, eliminar, etc).
	 *
	 * @param {string} action - Tipo de acción ('export', 'delete', etc.)
	 */
	const handleBulkAction = (action) => {
		if (action === 'export') {
			handleExportCandidates();
		} else if (action === 'delete') {
			handleBulkDeleteQuestion();
		}
	};

	const handleBulkDeleteQuestion = () => {
		Swal.fire({
			title: '¿Estás seguro de eliminar los candidatos seleccionados?',
			text: 'Esta acción eliminará permanentemente a los candidatos seleccionados del sistema. No podrás deshacer esta operación.',
			icon: "question",
			confirmButtonText: 'Continuar',
			cancelButtonText: 'Cancelar',
			showCancelButton: true,
			showCloseButton: true,
			allowOutsideClick: false,
			allowEscapeKey: false,
			allowEnterKey: false,
			reverseButtons : true
		}).then((result) => {
			if (result.isConfirmed) 
				for (const item of selectedRows) {
					handleBulkDelete(item.id);
				}
		}).catch(() => {
			return false;
		})
	}

	/**
	 * 
	 */
	const handleExportCandidates = () => {
		Swal.fire({
			title: '¿Deseas descargar el reporte de los candidatos?',
			text: 'Se generará un archivo con la información detallada de los candidatos.',
			icon: "question",
			confirmButtonText: 'Continuar',
			cancelButtonText: 'Cancelar',
			showCancelButton: true,
			showCloseButton: true,
			allowOutsideClick: false,
			allowEscapeKey: false,
			allowEnterKey: false,
			reverseButtons : true
		}).then((result) => {
			if (result.isConfirmed) 
				exportCandidates(selectedRows);
		}).catch(() => {
			return false;
		})
	}


	/**
	 * Maneja la acción de eliminación en lote con validación por estatus de certificación.
	 */
	const handleBulkDelete = async () => {
		const notDeletable = selectedRows.filter(item => item.certification_status !== "PENDIENTE");
		const deletable = selectedRows.filter(item => item.certification_status === "PENDIENTE");

		if (notDeletable.length > 0) {
			const names = notDeletable.map(item => item.name).join('<br>');

			const result = await Swal.fire({
				title: 'Alerta',
				icon: 'warning',
				html: `Los siguientes usuarios no pueden ser eliminados porque ya iniciaron su certificación:<br><br>${names}<br><br>¿Deseas continuar con los demás?`,
				showCancelButton: true,
				showCloseButton: true,
				confirmButtonText: 'Sí, continuar',
				cancelButtonText: 'Cancelar',
				allowOutsideClick: false,
				allowEscapeKey: false,
				allowEnterKey: false,
				reverseButtons: true
			});

			if (!result.isConfirmed) return;
		}

		setLoading(true);
		for (const item of deletable) {
			await handleDeleteCertification(item.user_id, item.certification_id);
		}
		setLoading(false);
	};

	return (
		<Row className="gx-3">
			{uploadCandidates ? (
				<div className="sweet-loading" style={{
					position: 'fixed',
					top: 0,
					left: 0,
					width: '100vw',
					height: '100vh',
					display: 'flex',
					justifyContent: 'center',
					alignItems: 'center',
					backgroundColor: 'rgba(255, 255, 255, 0.8)',
					zIndex: 9999
				}} >
					<CircleLoader
						color={"#0a83e6"}
						loading={uploadCandidates}
						cssOverride={override}
						size={150}
						aria-label="Loading..."
						data-testid="loader"
					/>
				</div>
			) : (
				<>
					<Col xxl={12} xl={12}>
						<AdvanceTableWrapper
							columns={CandidatesColumns(handleEdit, handleResetPassword, handleDelete)}
							data={rows}
							selection
							selectionColumnWidth={52}
							sortable
							pagination
							perPage={10}
							rowCount={rows.length}
						>
							<Card>
								<Card.Header className="px-0">
									<Header table handleSelectedChange={handleSelectedChange}  handleOpen={handleOpenModal} handleLoad={handleSubmitCandidates} onBulkAction={handleBulkAction} />
								</Card.Header>
								<Card.Body className="p-0">
									{ loading ? (
										<div className="sweet-loading" style={{
											position: 'fixed',
											top: 0,
											left: 0,
											width: '100vw',
											height: '100vh',
											display: 'flex',
											justifyContent: 'center',
											alignItems: 'center',
											backgroundColor: 'rgba(255, 255, 255, 0.8)', // Puedes agregar un fondo semitransparente si lo deseas
											zIndex: 9999}} >
											<BeatLoader
												color={"#0a83e6"}
												loading={loading}
												cssOverride={override}
												size={15}
												margin={5}
												aria-label="Loading..."
												data-testid="loader"
											/>
										</div>
									) : (
										<AdvanceTable
											table
											headerClassName="bg-light text-800 align-middle"
											rowClassName="btn-reveal-trigger align-middle"
											tableProps={{
												size: 'sm',
												className: 'fs--1 mb-0 overflow-hidden'
											}}
										/>
									)}
								</Card.Body>
								<Card.Footer>
									<AdvanceTablePagination table />
								</Card.Footer>
							</Card>
						</AdvanceTableWrapper>
					</Col>
					{isModalOpen && activeModal === "Candidato" && (
						<CandidateModal
							candidate_id={candidateId}
							certification_id={certificationId}
							action={action}
							handleClose={handleCloseModal}
						/>
					)}
				</>
			)}
		</Row>
	);
};

export default CandidatesTable;